import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from 'react';

// Define the shape of the user data
interface User {
  id: string;
  name: string;
  email: string;
}

// Define the shape of the context
interface UserContextType {
  user: User | null;
  token: string | null;
  setUser: (user: User | null) => void;
  setToken: (token: string | null) => void;
}

// Create the context with default values
const UserContext = createContext<UserContextType | undefined>(undefined);

// Custom hook to use the UserContext
export const useUserContext = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUserContext must be used within a UserProvider');
  }
  return context;
};

// Provider component
interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);

  // Rehydrate auth state from localStorage on mount
  useEffect(() => {
    const storedToken = localStorage.getItem('mrmtoken');
    const storedUser = localStorage.getItem('user');

    if (storedToken) {
      try {
        // Only parse if it's a JSON string, otherwise use as is
        const parsedToken = storedToken.startsWith('{')
          ? JSON.parse(storedToken)
          : storedToken;
        setToken(parsedToken);
      } catch (e) {
        console.error('Error parsing token:', e);
        setToken(null);
      }
    }

    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);
      } catch (e) {
        console.error('Error parsing user:', e);
        setUser(null);
      }
    }
  }, []);

  return (
    <UserContext.Provider value={{ user, token, setUser, setToken }}>
      {children}
    </UserContext.Provider>
  );
};
